import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ttsService } from '../services/ttsService';
import type { TtsAudioChunkDto, TtsVoiceConfig } from '../services/ttsService';

/**
 * TTS Pre-fetching Configuration
 */
const TTS_CONFIG = {
  INITIAL_PREFETCH_COUNT: 5,     // Chunks to fetch before starting playback
  ROLLING_BUFFER_SIZE: 3,        // Chunks to keep ahead during playback
  PREFETCH_TRIGGER_THRESHOLD: 2, // Trigger prefetch when this many chunks remain in buffer
  MAX_RETRY_ATTEMPTS: 3,         // Retry failed chunk generation
  RETRY_DELAY_MS: 1000,          // Delay between retries (exponential backoff)
} as const;

/**
 * TTS playback state
 */
export type TtsPlaybackState = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

/**
 * TTS provider type
 */
export type TtsProvider = 'server' | 'web-speech';

/**
 * TTS settings (user preferences)
 */
export interface TtsSettings {
  voiceId: string; // Changed from voiceGender - now stores actual voice ID
  speed: number; // 0.5 to 2.0
  volume: number; // 0 to 1
}

/**
 * TTS buffer status (for pre-fetching management)
 */
export interface TtsBufferStatus {
  isBuffering: boolean;           // Whether initial buffering is in progress
  bufferedChunks: number[];       // Array of chunk indices ready to play
  loadingChunks: number[];        // Array of chunk indices currently loading
  failedChunks: number[];         // Array of chunk indices that failed to load
}

/**
 * TTS Context state
 */
interface TtsContextState {
  // Playback state
  isActive: boolean; // Whether TTS mode is active
  playbackState: TtsPlaybackState;
  currentBookPageTextId: string | null;
  currentChunkIndex: number;
  totalChunks: number;
  currentTime: number; // Current position in seconds
  duration: number; // Total duration of current chunk in seconds

  // Settings
  settings: TtsSettings;
  availableVoices: TtsVoiceConfig[]; // Available voices from backend
  isLoadingVoices: boolean;

  // Chunks
  chunks: TtsAudioChunkDto[];
  currentChunk: TtsAudioChunkDto | null;

  // Buffer management (for seamless playback)
  bufferStatus: TtsBufferStatus;

  // Actions
  activateTts: (bookPageTextId: string) => Promise<void>;
  deactivateTts: () => void;
  play: () => void;
  pause: () => void;
  skipForward: () => void;
  skipBackward: () => void;
  seekToChunk: (chunkIndex: number) => void;
  updateSettings: (settings: Partial<TtsSettings>) => void;
  loadAvailableVoices: () => Promise<void>;
}

const TtsContext = createContext<TtsContextState | undefined>(undefined);

/**
 * TTS Provider Component
 */
export const TtsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get current route location to detect navigation
  const location = useLocation();

  // State
  const [isActive, setIsActive] = useState(false);
  const [playbackState, setPlaybackState] = useState<TtsPlaybackState>('idle');
  const [provider, setProvider] = useState<TtsProvider>('server'); // Default to server-side
  const [currentBookPageTextId, setCurrentBookPageTextId] = useState<string | null>(null);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [chunks, setChunks] = useState<TtsAudioChunkDto[]>([]);
  const [currentChunk, setCurrentChunk] = useState<TtsAudioChunkDto | null>(null);
  const [currentText, setCurrentText] = useState<string>(''); // For Web Speech API
  const [availableVoices, setAvailableVoices] = useState<TtsVoiceConfig[]>([]);
  const [bufferStatus, setBufferStatus] = useState<TtsBufferStatus>({
    isBuffering: false,
    bufferedChunks: [],
    loadingChunks: [],
    failedChunks: [],
  });
  const [isLoadingVoices, setIsLoadingVoices] = useState(true);
  const [settings, setSettings] = useState<TtsSettings>(() => {
    // Load from localStorage or use default
    const savedSettings = localStorage.getItem('tts-settings');
    if (savedSettings) {
      try {
        return JSON.parse(savedSettings);
      } catch {
        // Fall through to default
      }
    }
    return {
      voiceId: '', // Will be set to default voice after loading
      speed: 1.0,
      volume: 1.0
    };
  });

  // Audio element ref (for server-side TTS)
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Web Speech API ref (for client-side TTS)
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Refs to store latest values for event handlers (avoid stale closures)
  const currentChunkIndexRef = useRef(currentChunkIndex);
  const totalChunksRef = useRef(totalChunks);
  const playbackStateRef = useRef(playbackState);
  const bufferedChunksRef = useRef(bufferStatus.bufferedChunks);
  const isInitialMount = useRef(true); // Track initial mount to avoid deactivating on first render

  // Keep refs in sync with state
  useEffect(() => {
    currentChunkIndexRef.current = currentChunkIndex;
  }, [currentChunkIndex]);

  useEffect(() => {
    totalChunksRef.current = totalChunks;
  }, [totalChunks]);

  useEffect(() => {
    playbackStateRef.current = playbackState;
  }, [playbackState]);

  useEffect(() => {
    bufferedChunksRef.current = bufferStatus.bufferedChunks;
  }, [bufferStatus.bufferedChunks]);

  /**
   * Audio event handlers (defined before useEffect that uses them)
   */
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  }, []);

  const handleAudioEnded = useCallback(() => {
    // Use refs to get latest values (avoid stale closure issues)
    const currentChunk = currentChunkIndexRef.current;
    const total = totalChunksRef.current;
    const state = playbackStateRef.current;
    const buffered = bufferedChunksRef.current;

    console.log(`[TTS DEBUG] ========== AUDIO ENDED ==========`);
    console.log(`[TTS DEBUG] Current chunk: ${currentChunk}, Total chunks: ${total}`);
    console.log(`[TTS DEBUG] Playback state: ${state}`);
    console.log(`[TTS DEBUG] Buffered chunks: [${buffered.join(', ')}]`);

    // Auto-advance to next chunk
    if (currentChunk < total - 1) {
      const nextChunk = currentChunk + 1;

      // Check if next chunk is buffered (should be ready due to rolling buffer)
      const isBuffered = buffered.includes(nextChunk);

      console.log(`[TTS DEBUG] Next chunk: ${nextChunk}, Is buffered: ${isBuffered}`);

      if (!isBuffered) {
        console.warn(`[TTS DEBUG] ⚠️ Next chunk ${nextChunk} not buffered yet, may cause brief pause`);
      }

      console.log(`[TTS DEBUG] Setting currentChunkIndex to ${nextChunk}...`);
      setCurrentChunkIndex(nextChunk);
    } else {
      // Reached end of all chunks
      console.log('[TTS DEBUG] ✅ Reached end of chapter');
      setPlaybackState('idle');
      setCurrentChunkIndex(0);

      // Clear buffer for next playback
      setBufferStatus({
        isBuffering: false,
        bufferedChunks: [],
        loadingChunks: [],
        failedChunks: [],
      });
    }
  }, []); // No dependencies - uses refs instead

  const handleAudioError = useCallback((e: Event) => {
    console.error('Audio playback error:', e);
    setPlaybackState('error');
  }, []);

  /**
   * Initialize audio element (once on mount)
   */
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'auto';
      console.log('[TTS DEBUG] Audio element created');
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  /**
   * Attach event listeners to audio element
   * Re-attaches if handlers change (though all handlers are now stable)
   */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    console.log('[TTS DEBUG] Attaching event listeners to audio element');

    // Event listeners
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleAudioEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleAudioError);

    return () => {
      console.log('[TTS DEBUG] Removing event listeners from audio element');
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleAudioEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleAudioError);
    };
  }, [handleTimeUpdate, handleAudioEnded, handleLoadedMetadata, handleAudioError]);

  /**
   * Apply settings to audio element
   */
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = settings.speed;
      audioRef.current.volume = settings.volume;
    }
  }, [settings.speed, settings.volume]);

  /**
   * Load available voices from backend
   */
  const loadAvailableVoices = useCallback(async () => {
    try {
      setIsLoadingVoices(true);
      const voices = await ttsService.getAvailableVoices();
      setAvailableVoices(voices);

      // If no voice is selected yet, set the default voice
      if (!settings.voiceId && voices.length > 0) {
        const defaultVoice = voices.find(v => v.isDefault) || voices[0];
        setSettings(prev => ({
          ...prev,
          voiceId: defaultVoice.voiceId
        }));
      }
    } catch (error) {
      console.error('Error loading available voices:', error);
      // On error, keep empty array - will fall back to Web Speech API
    } finally {
      setIsLoadingVoices(false);
    }
  }, [settings.voiceId]);

  /**
   * Load available voices on mount only
   * Note: Removed loadAvailableVoices from dependencies to prevent unnecessary re-fetching
   */
  useEffect(() => {
    loadAvailableVoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Save settings to localStorage when they change
   */
  useEffect(() => {
    if (settings.voiceId) {
      localStorage.setItem('tts-settings', JSON.stringify(settings));
    }
  }, [settings]);

  /**
   * Cleanup: Stop TTS when route changes (user navigates away)
   */
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Deactivate TTS when location changes (navigation)
    if (isActive) {
      console.log('[TTS Cleanup] Route changed, stopping TTS playback');
      deactivateTts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Only run when pathname changes

  /**
   * Cleanup: Stop TTS when page unloads (reload, close tab, etc.)
   */
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isActive) {
        console.log('[TTS Cleanup] Page unloading, stopping TTS playback');
        // Stop audio playback immediately
        if (provider === 'server' && audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = '';
        } else if (provider === 'web-speech') {
          window.speechSynthesis.cancel();
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isActive, provider]);

  /**
   * Prefetch initial chunks aggressively (5 chunks before playback starts)
   */
  const prefetchInitialChunks = useCallback(async (bookPageTextId: string, startChunk: number): Promise<boolean> => {
    const endChunk = Math.min(startChunk + TTS_CONFIG.INITIAL_PREFETCH_COUNT - 1, totalChunks - 1);

    console.log(`[TTS Prefetch] Initial prefetch: chunks ${startChunk}-${endChunk}`);

    setBufferStatus(prev => ({
      ...prev,
      isBuffering: true,
      loadingChunks: Array.from({ length: endChunk - startChunk + 1 }, (_, i) => startChunk + i),
    }));

    try {
      // Prefetch chunks in parallel using the existing prefetchChunks service method
      const result = await ttsService.prefetchChunks(
        bookPageTextId,
        startChunk,
        TTS_CONFIG.INITIAL_PREFETCH_COUNT,
        settings.voiceId
      );

      // Update buffer status with successfully loaded chunks
      const successfulChunks = result
        .map((chunk, idx) => chunk ? startChunk + idx : null)
        .filter((idx): idx is number => idx !== null);

      setBufferStatus(prev => ({
        ...prev,
        isBuffering: false,
        bufferedChunks: successfulChunks,
        loadingChunks: [],
      }));

      console.log(`[TTS Prefetch] Initial prefetch complete: ${successfulChunks.length} chunks ready`);
      return successfulChunks.length > 0;
    } catch (error) {
      console.error('[TTS Prefetch] Initial prefetch failed:', error);
      setBufferStatus(prev => ({ ...prev, isBuffering: false, loadingChunks: [] }));
      return false;
    }
  }, [totalChunks, settings.voiceId]);

  /**
   * Maintain rolling buffer (keep 3 chunks ahead during playback)
   */
  const maintainRollingBuffer = useCallback(async (bookPageTextId: string, currentChunk: number) => {
    const nextChunkStart = currentChunk + 1;
    const nextChunkEnd = Math.min(
      currentChunk + TTS_CONFIG.ROLLING_BUFFER_SIZE,
      totalChunks - 1
    );

    // Determine which chunks need to be fetched
    const chunksToFetch: number[] = [];
    for (let i = nextChunkStart; i <= nextChunkEnd; i++) {
      const isBuffered = bufferStatus.bufferedChunks.includes(i);
      const isLoading = bufferStatus.loadingChunks.includes(i);
      const isFailed = bufferStatus.failedChunks.includes(i);

      if (!isBuffered && !isLoading && !isFailed) {
        chunksToFetch.push(i);
      }
    }

    if (chunksToFetch.length === 0) {
      return; // Buffer is healthy
    }

    console.log(`[TTS Buffer] Fetching chunks to maintain buffer:`, chunksToFetch);

    setBufferStatus(prev => ({
      ...prev,
      loadingChunks: [...prev.loadingChunks, ...chunksToFetch],
    }));

    // Fetch missing chunks in parallel
    const fetchPromises = chunksToFetch.map(chunkIndex =>
      ttsService.generateChunk({
        bookPageTextId,
        chunkIndex,
        voiceId: settings.voiceId
      })
        .then(() => {
          setBufferStatus(prev => ({
            ...prev,
            bufferedChunks: [...prev.bufferedChunks, chunkIndex],
            loadingChunks: prev.loadingChunks.filter(i => i !== chunkIndex),
          }));
        })
        .catch(err => {
          console.error(`[TTS Buffer] Failed to fetch chunk ${chunkIndex}:`, err);
          setBufferStatus(prev => ({
            ...prev,
            failedChunks: [...prev.failedChunks, chunkIndex],
            loadingChunks: prev.loadingChunks.filter(i => i !== chunkIndex),
          }));
        })
    );

    await Promise.allSettled(fetchPromises);
  }, [totalChunks, bufferStatus.bufferedChunks, bufferStatus.loadingChunks, bufferStatus.failedChunks, settings.voiceId]);

  /**
   * Retry failed chunk with exponential backoff
   */
  const retryFailedChunk = useCallback(async (bookPageTextId: string, chunkIndex: number, attempt = 1): Promise<boolean> => {
    if (attempt > TTS_CONFIG.MAX_RETRY_ATTEMPTS) {
      console.error(`[TTS Retry] Max retries reached for chunk ${chunkIndex}`);
      return false;
    }

    console.log(`[TTS Retry] Retrying chunk ${chunkIndex}, attempt ${attempt}/${TTS_CONFIG.MAX_RETRY_ATTEMPTS}`);

    try {
      // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, TTS_CONFIG.RETRY_DELAY_MS * attempt));

      await ttsService.generateChunk({
        bookPageTextId,
        chunkIndex,
        voiceId: settings.voiceId
      });

      // Remove from failed, add to buffered
      setBufferStatus(prev => ({
        ...prev,
        failedChunks: prev.failedChunks.filter(i => i !== chunkIndex),
        bufferedChunks: [...prev.bufferedChunks, chunkIndex],
      }));

      console.log(`[TTS Retry] Chunk ${chunkIndex} retry successful`);
      return true;
    } catch (error) {
      console.error(`[TTS Retry] Chunk ${chunkIndex} retry ${attempt} failed:`, error);
      return retryFailedChunk(bookPageTextId, chunkIndex, attempt + 1);
    }
  }, [settings.voiceId]);

  /**
   * Load and play a specific chunk
   */
  const loadChunk = useCallback(async (chunkIndex: number) => {
    console.log(`[TTS DEBUG] ========== loadChunk(${chunkIndex}) called ==========`);
    console.log(`[TTS DEBUG] currentBookPageTextId: ${currentBookPageTextId}`);
    console.log(`[TTS DEBUG] playbackState before loading: ${playbackState}`);

    if (!currentBookPageTextId) {
      console.log(`[TTS DEBUG] ❌ No currentBookPageTextId, returning`);
      return;
    }

    try {
      console.log(`[TTS DEBUG] Setting playbackState to 'loading'...`);
      setPlaybackState('loading');

      // Check if chunk is already buffered (prefetched)
      const isAlreadyBuffered = bufferStatus.bufferedChunks.includes(chunkIndex);

      console.log(`[TTS DEBUG] Chunk ${chunkIndex} already buffered: ${isAlreadyBuffered}`);

      // Generate/fetch chunk
      // Note: May return 409 if chunk was just created by parallel request
      let chunk;
      try {
        console.log(`[TTS DEBUG] Calling ttsService.generateChunk for chunk ${chunkIndex}...`);
        chunk = await ttsService.generateChunk({
          bookPageTextId: currentBookPageTextId,
          chunkIndex,
          voiceId: settings.voiceId
        });
        console.log(`[TTS DEBUG] ✅ Chunk ${chunkIndex} fetched successfully`);
      } catch (error: any) {
        // Handle 409 Conflict - chunk already exists, fetch it instead
        if (error?.response?.status === 409) {
          console.log(`[TTS DEBUG] Chunk ${chunkIndex} already exists (409), retrying...`);

          // Retry the request - backend should return existing chunk
          await new Promise(resolve => setTimeout(resolve, 100)); // Brief delay
          chunk = await ttsService.generateChunk({
            bookPageTextId: currentBookPageTextId,
            chunkIndex,
            voiceId: settings.voiceId
          });
          console.log(`[TTS DEBUG] ✅ Chunk ${chunkIndex} fetched on retry`);
        } else {
          console.error(`[TTS DEBUG] ❌ Error fetching chunk ${chunkIndex}:`, error);
          throw error; // Re-throw other errors
        }
      }

      setCurrentChunk(chunk);
      console.log(`[TTS DEBUG] Current chunk set to:`, chunk);

      // Load audio
      if (audioRef.current && chunk.id) {
        const audioUrl = ttsService.getAudioStreamUrl(chunk.id);
        console.log(`[TTS DEBUG] Audio URL: ${audioUrl}`);
        console.log(`[TTS DEBUG] Setting audio src and loading...`);
        audioRef.current.src = audioUrl;
        await audioRef.current.load();
        console.log(`[TTS DEBUG] ✅ Audio loaded successfully`);

        // Auto-play after loading
        console.log(`[TTS DEBUG] Checking playback state for auto-play...`);
        console.log(`[TTS DEBUG] playbackState: ${playbackState}`);
        console.log(`[TTS DEBUG] Should auto-play: ${playbackState !== 'paused'}`);

        if (playbackState !== 'paused') {
          console.log(`[TTS DEBUG] Attempting to play audio...`);
          try {
            await audioRef.current.play();
            console.log(`[TTS DEBUG] ✅ Audio playing successfully`);
            setPlaybackState('playing');
          } catch (playError) {
            console.error(`[TTS DEBUG] ❌ Error playing audio:`, playError);
            throw playError;
          }
        } else {
          console.log(`[TTS DEBUG] ⏸️ Playback state is 'paused', not auto-playing`);
        }
      } else {
        console.log(`[TTS DEBUG] ❌ Cannot load audio: audioRef=${!!audioRef.current}, chunk.id=${chunk?.id}`);
      }

      // Maintain rolling buffer (3 chunks ahead)
      console.log(`[TTS DEBUG] Maintaining rolling buffer for chunk ${chunkIndex}...`);
      await maintainRollingBuffer(currentBookPageTextId, chunkIndex);
    } catch (error: any) {
      // Check if error is Free tier limitation
      const errorCode = error?.response?.data?.error?.code;
      const errorMessage = error?.userMessage || error?.response?.data?.error?.message || error.message;

      if (errorCode === 'TTS:FreeTierOnly') {
        console.log('Server-side TTS not available, falling back to Web Speech API');

        try {
          // Fall back to Web Speech API
          if (!window.speechSynthesis) {
            throw new Error('Web Speech API not supported in this browser');
          }

          // Fetch the text content for Web Speech
          const response = await ttsService.getBookPageText(currentBookPageTextId);
          setCurrentText(response.plainText || '');

          // Switch to Web Speech API
          setProvider('web-speech');
          setTotalChunks(1); // Web Speech reads entire text as one chunk
          setCurrentChunkIndex(0);
          setPlaybackState('idle'); // Ready to play
        } catch (fallbackError) {
          console.error('Failed to fall back to Web Speech API:', fallbackError);
          setPlaybackState('error');
          // TODO: Show error message to user via toast/notification
          alert(`TTS Error: ${errorMessage}`);
        }
      } else {
        console.error('Error loading chunk:', error);
        console.error('User-friendly error message:', errorMessage);
        setPlaybackState('error');
        // TODO: Replace alert with proper toast/notification component
        alert(`TTS Error: ${errorMessage}`);
      }
    }
  }, [currentBookPageTextId, settings.voiceId, totalChunks, playbackState]);

  /**
   * Load chunk when chunk index changes (only for server-side TTS)
   * Note: Removed loadChunk from dependencies to prevent re-triggering on state changes
   */
  useEffect(() => {
    console.log(`[TTS DEBUG] ========== useEffect: currentChunkIndex changed ==========`);
    console.log(`[TTS DEBUG] currentChunkIndex: ${currentChunkIndex}`);
    console.log(`[TTS DEBUG] isActive: ${isActive}`);
    console.log(`[TTS DEBUG] provider: ${provider}`);
    console.log(`[TTS DEBUG] playbackState: ${playbackState}`);

    if (isActive && currentChunkIndex >= 0 && provider === 'server') {
      console.log(`[TTS DEBUG] ✅ Conditions met, calling loadChunk(${currentChunkIndex})`);
      loadChunk(currentChunkIndex);
    } else {
      console.log(`[TTS DEBUG] ❌ Conditions NOT met, skipping loadChunk`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChunkIndex, isActive, provider]);

  /**
   * Reload current chunk when voice changes (immediate voice switching)
   * This allows users to hear the new voice immediately without waiting for next chunk
   */
  useEffect(() => {
    if (isActive && currentChunkIndex >= 0 && provider === 'server' && settings.voiceId) {
      console.log(`[TTS DEBUG] ========== Voice changed to ${settings.voiceId}, clearing buffer and reloading current chunk ==========`);

      // Clear buffer since all buffered chunks are in the old voice
      setBufferStatus({
        isBuffering: false,
        bufferedChunks: [],
        loadingChunks: [],
        failedChunks: [],
      });

      // Reload current chunk with new voice
      loadChunk(currentChunkIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.voiceId]);

  /**
   * Activate TTS for a BookPageText
   */
  const activateTts = useCallback(async (bookPageTextId: string) => {
    try {
      // Stop current playback if switching chapters
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }

      // Clear buffer state
      setBufferStatus({
        isBuffering: false,
        bufferedChunks: [],
        loadingChunks: [],
        failedChunks: [],
      });

      setPlaybackState('loading');
      setCurrentBookPageTextId(bookPageTextId);

      // Get total chunks
      const total = await ttsService.getTotalChunks(bookPageTextId);
      setTotalChunks(total);

      // Get selected voice info for gender (backward compat with getChunksByBookPageText)
      const selectedVoice = availableVoices.find(v => v.voiceId === settings.voiceId);
      const voiceGender = selectedVoice?.gender || 'Neutral';

      // Get existing chunks (check cache)
      const existingChunks = await ttsService.getChunksByBookPageText(
        bookPageTextId,
        voiceGender
      );
      setChunks(existingChunks);

      // Start with server-side TTS (will fall back to Web Speech in loadChunk if needed)
      setProvider('server');
      setCurrentChunkIndex(0);
      setIsActive(true);
    } catch (error) {
      console.error('Error activating TTS:', error);
      setPlaybackState('error');
    }
  }, [settings.voiceId, availableVoices]);

  /**
   * Deactivate TTS
   */
  const deactivateTts = useCallback(() => {
    if (provider === 'server' && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    } else if (provider === 'web-speech') {
      window.speechSynthesis.cancel();
    }
    setIsActive(false);
    setPlaybackState('idle');
    setProvider('server');
    setCurrentBookPageTextId(null);
    setCurrentChunkIndex(0);
    setTotalChunks(0);
    setCurrentChunk(null);
    setChunks([]);
    setCurrentText('');
  }, [provider]);

  /**
   * Play
   */
  const play = useCallback(async () => {
    console.log(`[TTS DEBUG] ========== play() called by user ==========`);
    console.log(`[TTS DEBUG] provider: ${provider}`);
    console.log(`[TTS DEBUG] currentChunkIndex: ${currentChunkIndex}`);
    console.log(`[TTS DEBUG] playbackState: ${playbackState}`);

    if (provider === 'server') {
      // Server-side TTS playback
      if (!currentBookPageTextId) {
        console.error('[TTS DEBUG] ❌ No chapter selected');
        return;
      }

      // Check if initial prefetch is needed (buffer is empty)
      const needsInitialPrefetch = bufferStatus.bufferedChunks.length === 0;

      console.log(`[TTS DEBUG] Needs initial prefetch: ${needsInitialPrefetch}`);

      if (needsInitialPrefetch) {
        console.log('[TTS DEBUG] Starting initial prefetch...');
        const prefetchSuccess = await prefetchInitialChunks(currentBookPageTextId, currentChunkIndex);

        if (!prefetchSuccess) {
          console.error('[TTS DEBUG] ❌ Initial prefetch failed, cannot start playback');
          setPlaybackState('error');
          return;
        }
        console.log('[TTS DEBUG] ✅ Initial prefetch completed successfully');
      }

      // Now play audio (loadChunk will be called via useEffect when currentChunkIndex changes)
      if (audioRef.current && audioRef.current.src) {
        console.log(`[TTS DEBUG] Audio ref exists with src: ${audioRef.current.src}`);
        try {
          console.log('[TTS DEBUG] Attempting to play audio...');
          await audioRef.current.play();
          console.log('[TTS DEBUG] ✅ Audio playing');
          setPlaybackState('playing');
        } catch (error) {
          console.error('[TTS DEBUG] ❌ Error playing audio:', error);
          setPlaybackState('error');
        }
      } else {
        console.log(`[TTS DEBUG] ⚠️ Cannot play - audioRef.current: ${!!audioRef.current}, src: ${audioRef.current?.src}`);
      }
    } else if (provider === 'web-speech') {
      // Web Speech API playback
      if (!currentText) {
        console.error('No text to speak');
        return;
      }

      // Create utterance
      const utterance = new SpeechSynthesisUtterance(currentText);
      utteranceRef.current = utterance;

      // Get voice based on selected voice's gender (for Web Speech fallback)
      const selectedVoice = availableVoices.find(v => v.voiceId === settings.voiceId);
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice =>
        selectedVoice?.gender === 'Female'
          ? voice.name.toLowerCase().includes('female') || voice.name.toLowerCase().includes('samantha')
          : voice.name.toLowerCase().includes('male') || voice.name.toLowerCase().includes('alex')
      );
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }

      // Apply settings
      utterance.rate = settings.speed;
      utterance.volume = settings.volume;

      // Event handlers
      utterance.onstart = () => {
        setPlaybackState('playing');
      };

      utterance.onend = () => {
        setPlaybackState('idle');
      };

      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        setPlaybackState('error');
      };

      // Speak
      window.speechSynthesis.speak(utterance);
    }
  }, [
    provider,
    currentBookPageTextId,
    currentChunkIndex,
    bufferStatus.bufferedChunks,
    prefetchInitialChunks,
    currentText,
    settings,
    availableVoices,
  ]);

  /**
   * Pause
   */
  const pause = useCallback(() => {
    if (provider === 'server' && audioRef.current) {
      audioRef.current.pause();
      setPlaybackState('paused');
    } else if (provider === 'web-speech') {
      window.speechSynthesis.pause();
      setPlaybackState('paused');
    }
  }, [provider]);

  /**
   * Skip forward to next chunk
   */
  const skipForward = useCallback(() => {
    if (currentChunkIndex < totalChunks - 1) {
      setCurrentChunkIndex((prev) => prev + 1);
    }
  }, [currentChunkIndex, totalChunks]);

  /**
   * Skip backward to previous chunk
   */
  const skipBackward = useCallback(() => {
    if (currentChunkIndex > 0) {
      setCurrentChunkIndex((prev) => prev - 1);
    }
  }, [currentChunkIndex]);

  /**
   * Seek to specific chunk
   */
  const seekToChunk = useCallback((chunkIndex: number) => {
    if (chunkIndex >= 0 && chunkIndex < totalChunks) {
      setCurrentChunkIndex(chunkIndex);
    }
  }, [totalChunks]);

  /**
   * Update settings
   */
  const updateSettings = useCallback((newSettings: Partial<TtsSettings>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
  }, []);

  const value: TtsContextState = {
    isActive,
    playbackState,
    currentBookPageTextId,
    currentChunkIndex,
    totalChunks,
    currentTime,
    duration,
    settings,
    availableVoices,
    isLoadingVoices,
    chunks,
    currentChunk,
    bufferStatus,
    activateTts,
    deactivateTts,
    play,
    pause,
    skipForward,
    skipBackward,
    seekToChunk,
    updateSettings,
    loadAvailableVoices
  };

  return <TtsContext.Provider value={value}>{children}</TtsContext.Provider>;
};

/**
 * Hook to use TTS context
 */
export const useTts = (): TtsContextState => {
  const context = useContext(TtsContext);
  if (!context) {
    throw new Error('useTts must be used within TtsProvider');
  }
  return context;
};
